import React, { useState } from 'react';
import { Button, DatePicker, Input, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SectionCard from '../activity/components/SectionCard';
import { DATE_FORMAT } from '../activity/utils/activityFormUtils';
import { DRIVER_ID_REGEX, PHONE_REGEX, getDefaultDateRange, isRangeTooLong, toSearchParams } from './utils/riskLogUtils';

const FilterField: React.FC<{ label: string; width: number; children: React.ReactNode }> = ({ label, width, children }) => (
    <div style={{ width }}>
        <div className="text-[13px] text-[#595959] mb-[6px]">{label}</div>
        {children}
    </div>
);

const defaultRange = (): [Dayjs, Dayjs] => {
    const [start, end] = getDefaultDateRange();
    return [dayjs(start), dayjs(end)];
};

/**
 * 风控日志搜索页：司机ID / 手机号至少一项 + 美西日期（最长 31 天），条件全部带到结果页 URL。
 * 从结果页"修改查询条件"返回时按 URL 回填。
 */
const RiskLogSearchPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [driverId, setDriverId] = useState(() => searchParams.get('driverId') ?? '');
    const [phone, setPhone] = useState(() => searchParams.get('phone') ?? '');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
        const start = searchParams.get('startDate');
        const end = searchParams.get('endDate');
        return start && end ? [dayjs(start), dayjs(end)] : defaultRange();
    });

    const handleSearch = () => {
        const trimmedDriverId = driverId.trim();
        const trimmedPhone = phone.trim();
        if (!trimmedDriverId && !trimmedPhone) {
            message.warning(t('errors.validation.driverActivityRiskLog.driverRequired'));
            return;
        }
        if (trimmedDriverId && !DRIVER_ID_REGEX.test(trimmedDriverId)) {
            message.warning(t('errors.validation.driverActivityRiskLog.driverIdInvalid'));
            return;
        }
        if (trimmedPhone && !PHONE_REGEX.test(trimmedPhone)) {
            message.warning(t('errors.validation.driverActivityRiskLog.phoneInvalid'));
            return;
        }
        const startDate = dateRange[0].format(DATE_FORMAT);
        const endDate = dateRange[1].format(DATE_FORMAT);
        if (isRangeTooLong(startDate, endDate)) {
            message.warning(t('errors.validation.driverActivityRiskLog.dateRangeExceeded'));
            return;
        }
        const params = toSearchParams({ driverId: trimmedDriverId, phone: trimmedPhone, startDate, endDate });
        navigate(`/partner-control/activityRisk/risk-log/result?${params.toString()}`);
    };

    const handleReset = () => {
        setDriverId('');
        setPhone('');
        setDateRange(defaultRange());
        navigate('/partner-control/activityRisk/risk-log', { replace: true });
    };

    return (
        <div>
            <h1 className="text-2xl font-bold mb-1">{t('driverOps.riskLog.title')}</h1>
            <div className="text-[13px] text-[#8C8C8C] mb-4">{t('driverOps.riskLog.description')}</div>

            <div className="bg-white border border-[#EFEFEF] rounded-[10px] px-[22px] py-[18px] mb-[18px]">
                <div className="flex gap-[14px] items-end flex-wrap">
                    <FilterField label={t('driverOps.riskLog.search.driverId')} width={200}>
                        <Input
                            allowClear
                            value={driverId}
                            placeholder={t('driverOps.riskLog.search.driverIdPlaceholder') as string}
                            onChange={(e) => setDriverId(e.target.value)}
                            onPressEnter={handleSearch}
                        />
                    </FilterField>
                    <FilterField label={t('driverOps.riskLog.search.phone')} width={200}>
                        <Input
                            allowClear
                            value={phone}
                            maxLength={10}
                            placeholder={t('driverOps.riskLog.search.phonePlaceholder') as string}
                            onChange={(e) => setPhone(e.target.value)}
                            onPressEnter={handleSearch}
                        />
                    </FilterField>
                    <FilterField label={t('driverOps.riskLog.search.date')} width={280}>
                        <DatePicker.RangePicker
                            className="w-full"
                            allowClear={false}
                            format={DATE_FORMAT}
                            value={dateRange}
                            onChange={(range) => range?.[0] && range[1] && setDateRange([range[0], range[1]])}
                        />
                    </FilterField>
                    <Button type="primary" onClick={handleSearch}>
                        {t('common.search')}
                    </Button>
                    <Button onClick={handleReset}>{t('common.reset')}</Button>
                </div>
                <div className="text-[12px] text-[#8C8C8C] mt-[10px]">{t('driverOps.riskLog.search.hint')}</div>
            </div>

            <SectionCard title={t('driverOps.riskLog.title')}>
                <div className="py-10 text-center text-[#8C8C8C]">{t('driverOps.riskLog.search.empty')}</div>
            </SectionCard>
        </div>
    );
};

export default RiskLogSearchPage;
